import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import {
  getProjects, updateProject, getStudents, getGenerationStatus,
  generateCards, deleteStudent, reprocessStudent, downloadAllUrl, downloadStudentUrl,
  getTemplate, updateStudent, clearProjectOutputs, clearStudentOutputs, getStudentPhoto,
  bulkDeleteStudents,
} from '../api';
import { ALL_FIELDS, getFieldLabel, maxLenFor } from '../fields';
import { copyToClipboard } from '../clipboard';

function StatusBadge({ status }) {
  const map = { pending: 'badge-gray', processing: 'badge-blue', completed: 'badge-green', failed: 'badge-red' };
  return <span className={`badge ${map[status] || 'badge-gray'}`}>{status}</span>;
}

// Admin photo thumbnail: photos are auth-protected, so fetch as a blob and
// render via an object URL (revoked on unmount). Falls back to a placeholder.
function StudentPhoto({ projectId, student }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!student.photo_path) { setFailed(true); return; }
    let objectUrl;
    let cancelled = false;
    getStudentPhoto(projectId, student.id)
      .then((res) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setUrl(objectUrl);
      })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [projectId, student.id, student.photo_path]);

  if (failed || !student.photo_path) {
    return (
      <div style={{
        width: 38, height: 38, borderRadius: 5, background: 'var(--bg3)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 15, color: 'var(--text-muted)',
      }}>—</div>
    );
  }
  if (!url) {
    return <div style={{ width: 38, height: 38, borderRadius: 5, background: 'var(--bg3)' }} />;
  }
  return (
    <img
      src={url}
      alt={student.name}
      style={{ width: 38, height: 38, borderRadius: 5, objectFit: 'cover', border: '1px solid var(--border)', display: 'block' }}
    />
  );
}

function GenerationPanel({ project, onRefresh }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false); // 'pending' | 'all' | false

  const fetchStatus = useCallback(async () => {
    try { const r = await getGenerationStatus(project.id); setStatus(r.data); } catch {}
  }, [project.id]);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 4000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  const handleGenerate = async (mode) => {
    setLoading(mode);
    try { await generateCards(project.id, mode); await fetchStatus(); onRefresh(); }
    catch (err) { alert(err.response?.data?.detail || 'Generation failed'); }
    finally { setLoading(false); }
  };

  const handleClearOutputs = async () => {
    if (!window.confirm('Delete all generated card files for this project? Student data and photos are kept. Cards can be regenerated any time.')) return;
    try {
      const r = await clearProjectOutputs(project.id);
      const mb = (r.data.freed_bytes / 1024 / 1024).toFixed(1);
      alert(`Cleared. Freed ${mb} MB of disk space.`);
      await fetchStatus();
      onRefresh();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to clear outputs'); }
  };

  // Hand the URL to the browser's native downloader: it streams the zip to
  // disk with its own progress UI instead of buffering a blob in memory.
  const handleDownload = (format) => {
    window.location.assign(downloadAllUrl(project.id, format));
  };

  const total = status?.total || 0;
  const completed = status?.completed || 0;
  const pending = status?.pending || 0;
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;

  return (
    <div className="card">
      <h3 style={{ fontWeight: 600, marginBottom: 16, fontSize: '0.95rem' }}>Generation</h3>
      {status && total > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: '0.82rem' }}>
            <span className="text-muted">Progress</span>
            <span style={{ fontFamily: 'var(--mono)', color: 'var(--accent)' }}>{pct}%</span>
          </div>
          <div className="progress-bar"><div className="progress-fill" style={{ width: `${pct}%` }} /></div>
          <div className="grid-4" style={{ marginTop: 12, gap: 8 }}>
            {['pending', 'processing', 'completed', 'failed'].map((s) => (
              <div key={s} style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: 'var(--mono)', fontSize: '1.1rem', fontWeight: 600, color: s === 'completed' ? 'var(--accent)' : s === 'failed' ? 'var(--red)' : s === 'processing' ? 'var(--blue)' : 'var(--text3)' }}>
                  {status[s]}
                </div>
                <div className="stat-label">{s}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: completed > 0 ? 8 : 0 }}>
        <button
          className="btn btn-primary btn-sm"
          onClick={() => handleGenerate('pending')}
          disabled={!!loading || !project.template_id || pending === 0}
          title="Generate only entries that haven't been processed yet"
        >
          {loading === 'pending' ? <><span className="spinner" /> Generating…</> : `Generate Pending (${pending})`}
        </button>
        <button
          className="btn btn-ghost btn-sm"
          onClick={() => handleGenerate('all')}
          disabled={!!loading || !project.template_id}
          title="Reset all entries to pending and regenerate from scratch"
        >
          {loading === 'all' ? <><span className="spinner" /> Regenerating…</> : 'Regenerate All'}
        </button>
      </div>
      {completed > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', alignSelf: 'center' }}>Download all:</span>
          {['pdf', 'eps', 'svg'].map((fmt) => (
            <button key={fmt} className="btn btn-ghost btn-sm" onClick={() => handleDownload(fmt)}>
              ↓ {fmt.toUpperCase()}
            </button>
          ))}
        </div>
      )}
      {total > 0 && completed > 0 && (
        <div style={{ borderTop: '1px solid var(--border)', marginTop: 12, paddingTop: 12 }}>
          <button
            className="btn btn-danger btn-sm"
            onClick={handleClearOutputs}
            title="Free up disk space by deleting generated card files. Photos and student data are kept."
          >
            🗑 Clear Generated Files
          </button>
          <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 6 }}>
            Frees disk space. Student data &amp; photos stay. Cards can be regenerated any time.
          </p>
        </div>
      )}
      {!project.template_id && <p className="form-hint mt-4">Assign a template to this project before generating.</p>}
    </div>
  );
}

function EditStudentModal({ student, enabledFields, onClose, onSaved }) {
  const activeFields = ALL_FIELDS.filter((f) => !enabledFields || enabledFields[f.key]);
  const initialForm = Object.fromEntries(
    activeFields.map((f) => {
      const apiKey = f.key === 'student_name' ? 'name' : f.key;
      return [f.key, student[apiKey] || student[f.key] || ''];
    })
  );
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {};
      activeFields.forEach((f) => {
        const apiKey = f.key === 'student_name' ? 'name' : f.key;
        payload[apiKey] = form[f.key] || null;
      });
      const res = await updateStudent(student.project_id, student.id, payload);
      onSaved(res.data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <h2>Edit Entry</h2>
          <p>Changes reset the card to pending and require regeneration.</p>
        </div>
        {error && <div className="alert alert-error mb-4">{error}</div>}
        <form onSubmit={handleSave}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 16px', marginBottom: 16 }}>
            {activeFields.map((f) => (
              <div key={f.key} className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">{f.label}</label>
                <input
                  className="form-input"
                  value={form[f.key]}
                  maxLength={maxLenFor(f.key)}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                />
              </div>
            ))}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? <span className="spinner" /> : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DownloadRowBtn({ projectId, student }) {
  const [open, setOpen] = useState(false);

  const handle = (fmt) => {
    setOpen(false);
    window.location.assign(downloadStudentUrl(projectId, student.id, fmt));
  };

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button className="btn btn-ghost btn-sm" onClick={() => setOpen((o) => !o)}>
        ↓
      </button>
      {open && (
        <div
          style={{
            position: 'absolute', top: '100%', left: 0, zIndex: 50,
            background: 'var(--bg2)', border: '1px solid var(--border)',
            borderRadius: 6, padding: 4, minWidth: 80, boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          }}
          onMouseLeave={() => setOpen(false)}
        >
          {['pdf', 'eps', 'svg'].map((fmt) => (
            <button
              key={fmt}
              onClick={() => handle(fmt)}
              style={{
                display: 'block', width: '100%', padding: '5px 10px',
                background: 'none', border: 'none', cursor: 'pointer',
                textAlign: 'left', fontSize: '0.8rem', color: 'var(--text)',
                borderRadius: 4,
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg3)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
            >
              {fmt.toUpperCase()}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [template, setTemplate] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [linkCopied, setLinkCopied] = useState(false);

  const handleCopyUploadLink = async () => {
    const ok = await copyToClipboard(`${window.location.origin}/upload/${project.share_token}`);
    if (ok) {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 1500);
    } else {
      alert('Could not copy automatically. Link:\n' + `${window.location.origin}/upload/${project.share_token}`);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const [projs, studs] = await Promise.all([getProjects(), getStudents(id)]);
      const proj = projs.data.find((p) => p.id === parseInt(id));
      if (!proj) { navigate('/projects'); return; }
      setProject(proj);
      setStudents(studs.data);
      if (proj.template_id) {
        const tmpl = await getTemplate(proj.template_id);
        setTemplate(tmpl.data);
      }
    } catch { navigate('/projects'); }
    finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async (student) => {
    if (!window.confirm(`Delete ${student.name}?`)) return;
    try {
      await deleteStudent(id, student.id);
      setStudents((prev) => prev.filter((s) => s.id !== student.id));
      setSelectedIds((prev) => { const n = new Set(prev); n.delete(student.id); return n; });
    } catch { alert('Delete failed'); }
  };

  const toggleSelect = (sid) => {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (n.has(sid)) n.delete(sid); else n.add(sid);
      return n;
    });
  };

  const toggleSelectAll = (visibleRows) => {
    setSelectedIds((prev) => {
      const allSelected = visibleRows.length > 0 && visibleRows.every((s) => prev.has(s.id));
      if (allSelected) return new Set();
      return new Set(visibleRows.map((s) => s.id));
    });
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!window.confirm(`Delete ${ids.length} selected ${ids.length === 1 ? 'entry' : 'entries'}? This cannot be undone.`)) return;
    try {
      await bulkDeleteStudents(id, ids);
      setStudents((prev) => prev.filter((s) => !selectedIds.has(s.id)));
      setSelectedIds(new Set());
    } catch (err) { alert(err.response?.data?.detail || 'Bulk delete failed'); }
  };

  const handleDeleteAll = async () => {
    if (students.length === 0) return;
    if (!window.confirm(`Delete ALL ${students.length} entries in this project? This cannot be undone.`)) return;
    try {
      await bulkDeleteStudents(id, null);
      setStudents([]);
      setSelectedIds(new Set());
    } catch (err) { alert(err.response?.data?.detail || 'Delete all failed'); }
  };

  const handleReprocess = async (student) => {
    try { await reprocessStudent(id, student.id); fetchData(); }
    catch { alert('Reprocess failed'); }
  };

  const handleClearStudentOutputs = async (student) => {
    if (!window.confirm(`Delete generated card files for ${student.name || student.student_id}? They can be regenerated any time.`)) return;
    try {
      await clearStudentOutputs(id, student.id);
      setStudents((prev) => prev.map((s) => s.id === student.id
        ? { ...s, status: 'pending', generated_svg: null, generated_pdf: null, generated_eps: null }
        : s
      ));
    } catch { alert('Failed to clear files'); }
  };

  const handleStatusToggle = async () => {
    const newStatus = project.status === 'active' ? 'disabled' : 'active';
    try { const r = await updateProject(project.id, { status: newStatus }); setProject(r.data); }
    catch { alert('Failed to update status'); }
  };

  if (loading) {
    return (
      <Layout title="Project">
        <div style={{ display: 'flex', justifyContent: 'center', padding: 64 }}>
          <div className="spinner spinner-lg" />
        </div>
      </Layout>
    );
  }

  const enabledFields = project.enabled_fields;
  const visibleFields = ALL_FIELDS.filter((f) => !enabledFields || enabledFields[f.key]);

  const q = search.trim().toLowerCase();
  const filteredStudents = q
    ? students.filter((s) =>
        [s.name, s.student_id, s.father_name, s.mother_name, s.roll, s.section,
         s.mobile_number, s.class_name, s.index_no, s.nid_no, s.registration_no]
          .some((v) => (v || '').toString().toLowerCase().includes(q)))
    : students;
  const allVisibleSelected = filteredStudents.length > 0 && filteredStudents.every((s) => selectedIds.has(s.id));

  return (
    <Layout title={project?.name || 'Project'}>
      {editTarget && (
        <EditStudentModal
          student={editTarget}
          enabledFields={enabledFields}
          onClose={() => setEditTarget(null)}
          onSaved={(updated) => {
            setStudents((prev) => prev.map((s) => s.id === updated.id ? updated : s));
            setEditTarget(null);
          }}
        />
      )}

      <div className="page-header">
        <div className="page-header-text">
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/projects')} style={{ marginBottom: 4 }}>← Back</button>
          <h1>{project.name}</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>
            {students.length} {students.length === 1 ? 'entry' : 'entries'} &middot; {template ? template.name : 'No template assigned'}
            &middot; Created {new Date(project.created_at).toLocaleDateString()}
            {project.delivery_date && <> &middot; Due {new Date(project.delivery_date).toLocaleDateString()}</>}
          </p>
        </div>
        <div className="page-header-actions">
          <span className={`badge ${project.status === 'active' ? 'badge-green' : 'badge-gray'}`}>{project.status}</span>
          <button className="btn btn-ghost btn-sm" onClick={handleStatusToggle}>
            {project.status === 'active' ? 'Disable' : 'Enable'}
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/templates')}>
            {template ? 'Change Template' : 'Assign Template'}
          </button>
        </div>
      </div>

      {/* Project description */}
      {project.description && (
        <div className="card" style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', fontWeight: 600, marginBottom: 6 }}>Description</div>
          <p style={{ margin: 0, lineHeight: 1.6 }}>{project.description}</p>
        </div>
      )}

      <div className="grid-2" style={{ marginBottom: 24 }}>
        <GenerationPanel project={project} onRefresh={fetchData} />

        <div className="card">
          <h3 style={{ fontWeight: 600, marginBottom: 12, fontSize: '0.95rem' }}>Card Fields</h3>
          {visibleFields.length > 0 ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {visibleFields.map((f) => (
                <span key={f.key} className="badge badge-blue" style={{ fontSize: '0.82em' }}>{f.label}</span>
              ))}
            </div>
          ) : (
            <div className="text-muted text-sm">All fields enabled</div>
          )}

          <div style={{ marginTop: 16 }}>
            <h3 style={{ fontWeight: 600, marginBottom: 8, fontSize: '0.95rem' }}>Client Upload Link</h3>
            <div className="copy-field">
              <div className="copy-field-text">{window.location.origin}/upload/{project.share_token}</div>
              <button className="copy-btn" onClick={handleCopyUploadLink}>
                {linkCopied ? '✓ Copied' : 'Copy'}
              </button>
            </div>
            <a className="btn btn-ghost btn-sm" href={`/upload/${project.share_token}`} target="_blank" rel="noreferrer" style={{ marginTop: 8, display: 'inline-block' }}>
              Preview Form ↗
            </a>
          </div>
        </div>
      </div>

      {/* Students table */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
          <h3 style={{ fontWeight: 600, fontSize: '0.95rem' }}>
            Entries <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>
              ({q ? `${filteredStudents.length} of ${students.length}` : students.length})
            </span>
          </h3>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              className="form-input"
              placeholder="Search entries…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ padding: '5px 10px', fontSize: 13, minWidth: 180 }}
            />
            {selectedIds.size > 0 && (
              <button className="btn btn-danger btn-sm" onClick={handleBulkDelete}>
                Delete selected ({selectedIds.size})
              </button>
            )}
            {students.length > 0 && (
              <button className="btn btn-ghost btn-sm" onClick={handleDeleteAll} title="Delete every entry in this project">
                Delete all
              </button>
            )}
          </div>
        </div>

        {students.length === 0 ? (
          <div className="empty-state">
            <span style={{ fontSize: 32 }}>◈</span>
            <h3>No entries yet</h3>
            <p>Share the client upload link so people can submit their info.</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 28 }}>
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={() => toggleSelectAll(filteredStudents)}
                      title="Select all"
                    />
                  </th>
                  <th>Photo</th>
                  <th>Name</th>
                  <th>ID</th>
                  {visibleFields.filter((f) => !['student_name', 'student_id'].includes(f.key)).slice(0, 3).map((f) => (
                    <th key={f.key}>{f.label}</th>
                  ))}
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredStudents.length === 0 ? (
                  <tr>
                    <td colSpan={7 + Math.min(3, visibleFields.filter((f) => !['student_name', 'student_id'].includes(f.key)).length)}
                        style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 20 }}>
                      No entries match “{search}”.
                    </td>
                  </tr>
                ) : filteredStudents.map((s) => (
                  <tr key={s.id} style={selectedIds.has(s.id) ? { background: 'var(--bg2)' } : undefined}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(s.id)}
                        onChange={() => toggleSelect(s.id)}
                      />
                    </td>
                    <td><StudentPhoto projectId={id} student={s} /></td>
                    <td className="td-primary">{s.name}</td>
                    <td className="td-mono">{s.student_id}</td>
                    {visibleFields.filter((f) => !['student_name', 'student_id'].includes(f.key)).slice(0, 3).map((f) => {
                      const apiKey = f.key === 'student_name' ? 'name' : f.key;
                      return (
                        <td key={f.key}>{s[apiKey] || s[f.key] || <span className="text-muted">—</span>}</td>
                      );
                    })}
                    <td><StatusBadge status={s.status} /></td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => setEditTarget(s)}>Edit</button>
                        {s.status === 'completed' && (
                          <>
                            <DownloadRowBtn projectId={id} student={s} />
                            <button
                              className="btn btn-ghost btn-sm"
                              title="Delete generated card files to free disk space"
                              onClick={() => handleClearStudentOutputs(s)}
                            >🗑</button>
                          </>
                        )}
                        {s.status === 'failed' && (
                          <button className="btn btn-ghost btn-sm" onClick={() => handleReprocess(s)}>Retry</button>
                        )}
                        <button className="btn btn-danger btn-sm" onClick={() => handleDelete(s)}>✕</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
